import { Col, Form, Modal, Row } from "antd";
import TextArea from "antd/es/input/TextArea";
import i18n from "i18next";
import { type FC, useCallback, useState } from "react";

import type { SiteEntity } from "src/app/features/Site/types/SiteEntity";
import { useUpdateTableDataMutation } from "../../../../../components";
import { ActionBar } from "../../../../../components/ActionBar/ActionBar";
import { PrimaryButton, SecondaryButton } from "../../../../../components/Buttons/components/ActionButton";
import { CsvImportModalButton } from "../../../../../components/FeatureButtons/CsvImportModalButton";
import { RenderImportResult } from "../../../../../components/FeatureButtons/csvModalButtonUtils";
import type { ButtonProps, ImportResult } from "../../../../../components/FeatureButtons/types";
import { Checkbox, SelectDynamic } from "../../../../../components/Form/formComponents";
import { ProgressBar } from "../../../../../components/ProgressBar/ProgressBar";
import { terminalProfileSelectConfig } from "../../../../../components/SearchForm/FormSearchUtils";
import { getAssignedDataGroup } from "../../../../../core/router/auth";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { FORM_ACTION } from "../../../../../core/router/types/routerTypes";

export const TerminalBatchUpdateCsvModal: FC<ButtonProps<SiteEntity>> = ({ isDisabled }) => {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [importResult, setImportResult] = useState<ImportResult | null>(null);
    const [showAllErrors, setShowAllErrors] = useState(false);

    const [form] = Form.useForm();

    const currentDataGroup = getAssignedDataGroup();
    const batchUpdateUrl = `${ResourceName.DATA_GROUPS}/${currentDataGroup.code}/${ResourceName.TERMINALS}/${FORM_ACTION.BATCH_UPDATE_CSV}`;

    const [batchUpdateDataRequest] = useUpdateTableDataMutation();

    const showModal = useCallback(() => setIsModalVisible(true), []);
    const onCancel = useCallback(() => setIsModalVisible(false), []);

    const onOk = useCallback(async () => {
        setUploading(true);

        try {
            const values = await form.validateFields();

            const body = {
                terminals: values.terminalIds,
                terminalProfileId: Number(values.destinationTerminalProfileId),
                updateTerminalProfile: values.isUpdateTerminalProfile,
            };

            batchUpdateDataRequest({
                url: batchUpdateUrl,
                method: "PUT",
                body,
            })
                .unwrap()
                // @ts-ignore
                .then((result) => setImportResult(result))
                .catch((error) => {
                    setImportResult({
                        importErrors: [error?.data?.message || "batchUpdateData.modal.importError"],
                    });
                })
                .finally(() => setUploading(false));
        } catch (error) {
            setImportResult({
                importErrors: [typeof error === "string" ? error : "batchUpdateData.modal.importError"],
            });
            setUploading(false);
        }
    }, [form, batchUpdateDataRequest, batchUpdateUrl]);

    const onCsvContent = useCallback(
        (content: string) => {
            if (content) {
                form.setFieldsValue({ terminalIds: content });
            }
        },
        [form],
    );

    return (
        <>
            <SecondaryButton onClick={showModal} disabled={isDisabled}>
                {i18n.t("button.batchUpdateCsv")}
            </SecondaryButton>

            <Modal
                open={isModalVisible}
                title={i18n.t("batchUpdateData.modal.title")}
                onCancel={onCancel}
                footer={[
                    <SecondaryButton key="close" onClick={onCancel}>
                        {i18n.t("common.close")}
                    </SecondaryButton>,
                    <PrimaryButton key="import" onClick={onOk} disabled={uploading}>
                        {i18n.t("button.save")}
                    </PrimaryButton>,
                ]}
            >
                <Form form={form}>
                    <ActionBar className="column">
                        <p>{i18n.t("batchUpdateData.modal.message")}</p>
                        <Row style={{ gap: "8px", marginBottom: "16px" }}>
                            <Col span={16}>
                                <Form.Item
                                    name="destinationTerminalProfileId"
                                    rules={[{ required: true, message: i18n.t("batchUpdateData.modal.terminalProfileRequired") }]}
                                >
                                    <SelectDynamic
                                        fieldConfig={terminalProfileSelectConfig}
                                        name="destinationTerminalProfileId"
                                        label={i18n.t("batchUpdateData.terminalProfile")}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item name="isUpdateTerminalProfile" valuePropName="checked">
                                    <Checkbox
                                        label="batchUpdateData.updateProfile"
                                        readOnly={false}
                                        defaultChecked={false}
                                        name="isUpdateTerminalProfile"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.Item
                            name="terminalIds"
                            rules={[{ required: true, message: i18n.t("batchUpdateData.modal.terminalsRequired") }]}
                        >
                            <TextArea rows={4} placeholder={i18n.t("batchUpdateData.modal.textAreaPlaceholder")} name="terminalIds" />
                        </Form.Item>
                        <CsvImportModalButton callback={onCsvContent} />
                        {uploading && <ProgressBar />}
                        {importResult && (
                            <RenderImportResult
                                importResult={importResult}
                                showAllErrors={showAllErrors}
                                setShowAllErrors={setShowAllErrors}
                            />
                        )}
                    </ActionBar>
                </Form>
            </Modal>
        </>
    );
};
